import React, { useState, useEffect } from 'react';
import '../styles/playerlist.css';
import Navbar from '../components/navbar';
import { API_URL } from '../config';
import {
  Box, Container, Typography, Tabs, Tab, TextField, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import {
  HowToVote, Search
} from '@mui/icons-material';

const CLASS_NAMES = {
  1: 'Féca', 2: 'Osamodas', 3: 'Enutrof', 4: 'Sram',
  5: 'Xélor', 6: 'Ecaflip', 7: 'Eniripsa', 8: 'Iop',
  9: 'Cré', 10: 'Sadida', 11: 'Sacrieur', 12: 'Pandawa'
};

const CLASS_COLORS = {
  1: '#4CAF50', 2: '#8BC34A', 3: '#FF9800', 4: '#B67A3C',
  5: '#2196F3', 6: '#F44336', 7: '#00BCD4', 8: '#F44336',
  9: '#4CAF50', 10: '#8BC34A', 11: '#F44336', 12: '#FF9800'
};

const MEDAL_COLORS = ['#C9CED6', '#C0C0C0', '#CD7F32'];
const MEDAL_ICONS = ['\u{1F947}', '\u{1F948}', '\u{1F949}'];

const LADDER_CONFIGS = {
  votes: {
    label: 'Votes',
    icon: <HowToVote />,
    endpoint: '/player/ladder/votes',
    color: '#2196F3',
    columns: [
      { key: 'rank',         header: '#',           render: (_, i) => renderRank(i) },
      { key: 'name',         header: 'Compte',      render: (r) => renderName(r) },
      { key: 'total_votes',  header: 'Votes',       render: (r) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><HowToVote sx={{ color: '#2196F3', fontSize: '1rem' }} /><Typography sx={{ color: '#64B5F6', fontWeight: 700 }}>{r.total_votes}</Typography></Box> },
    ]
  },
};

function renderRank(index) {
  if (index < 3) {
    return (
      <Typography sx={{ fontSize: '1.3rem', textAlign: 'center' }}>{MEDAL_ICONS[index]}</Typography>
    );
  }
  return <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, textAlign: 'center' }}>{index + 1}</Typography>;
}

function renderName(row) {
  const color = CLASS_COLORS[row.class] || '#B67A3C';
  const className = CLASS_NAMES[row.class] || '?';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar sx={{ width: 32, height: 32, background: `${color}30`, border: `2px solid ${color}60`, fontSize: '0.7rem', color }}>
        {row.name?.charAt(0)?.toUpperCase()}
      </Avatar>
      <Box>
        <Typography sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{row.name}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color }}>{className}</Typography>
      </Box>
    </Box>
  );
}

function PlayerList() {
  const [activeTab, setActiveTab]     = useState('votes');
  const [data, setData]               = useState({});
  const [loading, setLoading]         = useState({});
  const [searchTerm, setSearchTerm]   = useState('');

  useEffect(() => {
    const cfg = LADDER_CONFIGS[activeTab];
    if (data[activeTab]) return;
    setLoading(l => ({ ...l, [activeTab]: true }));
    fetch(API_URL + cfg.endpoint)
      .then(res => res.json())
      .then(rows => setData(d => ({ ...d, [activeTab]: rows })))
      .catch(err => console.error(err))
      .finally(() => setLoading(l => ({ ...l, [activeTab]: false })));
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const cfg = LADDER_CONFIGS[activeTab];
  const rows = (data[activeTab] || []).filter(r =>
    r.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0f1729 0%, #1a1a2e 100%)' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ pt: 14, pb: 8 }}>

        {/* Titre */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" sx={{
            fontWeight: 900,
            background: 'linear-gradient(135deg, #B67A3C 0%, #E4E7EC 50%, #C9CED6 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            letterSpacing: 2, mb: 1
          }}>
            {'\u{1F3C6} CLASSEMENTS'}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Classement des Voyageurs les plus actifs • Open Avril 2026
          </Typography>
        </Box>

        {/* Onglets */}
        <Box sx={{ mb: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => { setActiveTab(v); setSearchTerm(''); }}
            textColor="inherit"
            sx={{
              '& .MuiTab-root': { color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'none', fontSize: '1rem' },
              '& .Mui-selected': { color: '#fff !important' },
              '& .MuiTabs-indicator': { background: cfg.color, height: 3, borderRadius: 2 }
            }}
          >
            {Object.entries(LADDER_CONFIGS).map(([key, c]) => (
              <Tab key={key} value={key} label={c.label} icon={c.icon} iconPosition="start"
                sx={{ '&.Mui-selected': { color: `${c.color} !important` } }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Barre de recherche */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Rechercher un joueur..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ color: 'rgba(255,255,255,0.4)', mr: 1 }} /> }}
            sx={{
              width: 280,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                background: 'rgba(31,37,68,0.6)',
                borderRadius: 2,
                '& fieldset': { borderColor: `${cfg.color}40` },
                '&:hover fieldset': { borderColor: `${cfg.color}80` },
                '&.Mui-focused fieldset': { borderColor: cfg.color }
              }
            }}
          />
        </Box>

        {/* Tableau */}
        <TableContainer component={Paper} sx={{
          background: 'rgba(15,23,41,0.8)',
          border: `1px solid ${cfg.color}30`,
          borderRadius: 3,
          backdropFilter: 'blur(10px)'
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ borderBottom: `2px solid ${cfg.color}40` }}>
                {cfg.columns.map(col => (
                  <TableCell key={col.key} sx={{ color: cfg.color, fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 1, border: 'none', background: `${cfg.color}10` }}>
                    {col.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading[activeTab] ? (
                <TableRow>
                  <TableCell colSpan={cfg.columns.length} sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', border: 'none', py: 6 }}>
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={cfg.columns.length} sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', border: 'none', py: 6 }}>
                    Aucun joueur trouvé
                  </TableCell>
                </TableRow>
              ) : rows.map((row, index) => (
                <TableRow key={row.id} sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  background: index < 3 ? `${MEDAL_COLORS[index]}08` : 'transparent',
                  transition: 'background 0.2s',
                  '&:hover': { background: `${cfg.color}10` }
                }}>
                  {cfg.columns.map(col => (
                    <TableCell key={col.key} sx={{ border: 'none', py: 1.5 }}>
                      {col.render(row, index)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > 0 && (
          <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', mt: 2, fontSize: '0.85rem' }}>
            {rows.length} joueur{rows.length > 1 ? 's' : ''} affiché{rows.length > 1 ? 's' : ''}
          </Typography>
        )}
      </Container>
    </Box>
  );
}

export default PlayerList;